import { useCallback } from 'react';
import { addTaskComment } from '@apis/schedule';
import type { ITaskAttachment, SiteId, TaskId } from '@interfaces/IScheduler';
import { useImmediateWritePersist } from './useImmediateWritePersist';

/** One comment to post (client-generated id → idempotent retry). */
export interface ICommentPost {
  id: string;
  text: string;
  attachments?: ITaskAttachment[];
  authorName?: string;
}

/**
 * Immediate-write persistence for a task's comments (phase 2 — the Comments tab
 * leaves the Save path entirely). Returns `post(comment)`, which POSTs the
 * comment through the granular `comments` endpoint on the shared
 * `immediateWriteChannel`. One channel per (task, COMMENT id): each post is its
 * own serialized/retried write, and the client-generated id makes a retry
 * idempotent (same id twice = one comment) — so keying per id can never coalesce
 * two distinct comments into one. A post given up is rolled back (the comment
 * leaves the thread) and a toast says so. No-op when `scheduleNumber` is absent (Router-free
 * tests) — the store still updates optimistically. Pending posts flush on
 * unmount / task switch.
 */
export function useTaskCommentsPersist(
  siteId: SiteId,
  scheduleNumber: string | undefined,
  taskId: TaskId,
): (comment: ICommentPost) => void {
  const { write } = useImmediateWritePersist(siteId, scheduleNumber, taskId);

  return useCallback(
    (comment: ICommentPost) => {
      write<ICommentPost>(['comment', comment.id], comment, (c, ctx) =>
        addTaskComment(ctx.siteId, ctx.scheduleNumber, ctx.taskId, c),
      );
    },
    [write],
  );
}
